import Badge from '@/app/common/components/Badge'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/app/common/components/Card'
import { Input } from '@/app/common/components/input/Input'
import Label from '@/app/common/components/Label'
import { SubmitButton } from '@/app/common/components/SubmitButton'
import { CloseIcon } from '@/app/common/icons/general'
import { ProviderPreviewSettings } from '@/schemas/provider.schema'
import { selectSelectedProgram } from '@/storage/programs/slice'
import { zodResolver } from '@hookform/resolvers/zod'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { z } from 'zod'
import { useUpdatePreviewSettings } from '../hooks/useUpdatePreviewSettings'
import FormattedPhoneInput from './FormattedPhoneInput'
import AdminAuthGuard from '../../../admin-auth-guard'

// Helper for URL protocol
// function addProtocolIfMissing(url: string) {
//   if (!url) return ''
//   if (!/^https?:\/\//i.test(url)) {
//     return 'https://' + url
//   }
//   return url
// }

const phoneRegex = /^\+?[1-9]\d{1,14}$/

const businessDetailsSchema = z.object({
  website: z
    .string()
    .min(1, 'Link is required.')
    .transform((val) => {
      let urlStr = val.trim()
      if (!/^https?:\/\//i.test(urlStr)) {
        urlStr = 'https://' + urlStr
      }
      return urlStr
    })
    .refine(
      (val) => {
        try {
          const parsedUrl = new URL(val)

          const hostname = parsedUrl.hostname
          const parts = hostname.split('.')
          return parts.length >= 2 && parts[parts.length - 1].length >= 2
        } catch (error) {
          return false
        }
      },
      {
        message: 'Please enter a valid URL.',
      }
    )
    .or(z.literal('')),
  phone_store: z
    .string()
    .regex(phoneRegex, 'Please enter a valid phone number')
    .optional()
    .or(z.literal('')),
  businessLevels: z.array(z.string()),
  acceptingNewClients: z.boolean(),
})

export type BusinessDetailsFormData = z.infer<typeof businessDetailsSchema>

export interface BusinessDetailsCardProps {
  provider: ProviderPreviewSettings
  providerId: string
}

const BusinessDetailsCard: React.FC<BusinessDetailsCardProps> = ({
  provider,
  providerId,
}) => {
  // Get the current program id from redux
  const programId = useSelector(selectSelectedProgram).id

  const form = useForm<BusinessDetailsFormData & { newBusinessLevel: string }>({
    resolver: zodResolver(businessDetailsSchema),
    defaultValues: {
      website: provider.website ?? '',
      phone_store: provider.phone_store ?? '',
      businessLevels: provider.meta_data_tags ?? [],
      acceptingNewClients: provider.accepting_new_clients === 1,
      newBusinessLevel: '',
    },
  })

  const storeMutation = useUpdatePreviewSettings({
    providerId,
  })

  // Add business level on Enter
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const newBusinessLevel = form.getValues('newBusinessLevel').trim()
    const businessLevels = form.getValues('businessLevels')
    if (e.key === 'Enter' && newBusinessLevel) {
      e.preventDefault()
      if (!businessLevels.includes(newBusinessLevel)) {
        const updated = [...businessLevels, newBusinessLevel]
        form.setValue('businessLevels', updated, { shouldValidate: true })
      }
      form.setValue('newBusinessLevel', '')
    }
  }

  // Remove business level
  const removeBusinessLevel = (level: string) => {
    const businessLevels = form.getValues('businessLevels')
    const updated = businessLevels.filter((l: string) => l !== level)
    form.setValue('businessLevels', updated, { shouldValidate: true })
  }

  const onSubmit = (
    data: BusinessDetailsFormData & { newBusinessLevel: string }
  ) => {
    storeMutation.mutate({
      program_id: programId,
      website: data.website,
      phone_store: data.phone_store,
      meta_data_tags: data.businessLevels,
      accepting_new_clients: data.acceptingNewClients ? 1 : 0,
    })
  }

  const businessLevels = form.watch('businessLevels')

  return (
    <Card id="business-details">
      <CardHeader>
        <h3 className="text-lg font-semibold text-gray-900">
          Business Details
        </h3>
      </CardHeader>
      <form id="business-details-form" onSubmit={form.handleSubmit(onSubmit)}>
        <CardContent>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <Label htmlFor="website">Website URL (Optional)</Label>
                <div className="flex items-center gap-1">
                  <Input
                    {...form.register('website')}
                    id="website"
                    type="text"
                    className="shadow-none"
                    placeholder="www.example.com"
                  />
                </div>
                {form.formState.errors.website && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.website.message}
                  </p>
                )}
              </div>
              <div>
                <Controller
                  control={form.control}
                  name="phone_store"
                  render={({ field }) => (
                    <FormattedPhoneInput
                      value={field.value}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                    />
                  )}
                />
                {form.formState.errors.phone_store && (
                  <p className="mt-1 text-sm text-red-600">
                    {form.formState.errors.phone_store.message}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label htmlFor="businessLevels">
                Meta Tags (Press Enter to add)
              </Label>
              <div className="space-y-2">
                <Input
                  placeholder="Type and press Enter to add a business level"
                  {...form.register('newBusinessLevel')}
                  onKeyDown={handleKeyDown}
                  className="shadow-none"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {businessLevels &&
                    businessLevels.map((level: string, index: number) => (
                      <Badge
                        key={index}
                        semanticColor="Neutral"
                        className="flex items-center gap-1 w-[6rem] justify-between truncate max-w-[6rem]"
                      >
                        <span className="max-w-[6rem] truncate">{level}</span>
                        <button
                          type="button"
                          onClick={() => removeBusinessLevel(level)}
                          className="ml-1 hover:text-red-500 focus:outline-none"
                        >
                          <CloseIcon />
                        </button>
                      </Badge>
                    ))}
                </div>
              </div>
              {form.formState.errors.businessLevels && (
                <p className="mt-1 text-sm text-red-600">
                  {form.formState.errors.businessLevels.message as string}
                </p>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-end py-4">
          <AdminAuthGuard requiredPermissions={['provider_management_edit']}>
            <div>
              <SubmitButton
                buttonTxt="Save changes"
                status={storeMutation.status}
              />
            </div>
          </AdminAuthGuard>
        </CardFooter>
      </form>
    </Card>
  )
}

export default BusinessDetailsCard
